<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import DialerPauseBar from './DialerPauseBar.vue';
import { useAciSipConnection } from 'dashboard/api/telephony_aci/useAciSipConnection';
import { useCallHistory } from 'dashboard/api/telephony_aci/useCallHistory';
import ContactsAPI from 'dashboard/api/contacts';
import TransferNotificationsAPI from 'dashboard/api/asterisk/transferNotifications';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const emit = defineEmits(['close']);
// Layout constants
const PANEL_WIDTH = 280;
// 450 cabia até a barra "Pausar" (commit 278035eb6) entrar acima do dialpad
// sem crescer a altura do painel — o botão "Ligar" ficava cortado pelo
// overflow-hidden (invisível, sem erro). 530 casa com o mesmo ajuste já
// feito em WhatsAppSipDialerPanel.vue (ver PANEL_EXPANDED_HEIGHT lá).
const PANEL_EXPANDED_HEIGHT = 530;
const PANEL_COMPACT_HEIGHT = 48;
const VIEWPORT_PADDING = 16;
const DEBOUNCE_DELAY = 300;
const DOUBLE_CLICK_GUARD_MS = 500;
const SHORT_GUARD_MS = 300;
const POST_CALL_COOLDOWN_MS = 1000;

const { t } = useI18n();
const store = useStore();

// Check if telephony feature is enabled for this account
const getCurrentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isTelephonyEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(
    getCurrentAccountId.value,
    FEATURE_FLAGS.ASTERISK_TELEPHONY
  );
});
const hasSipExtension = computed(() => !!currentUser.value?.sip_extension);
const canUseDialer = computed(
  () => isTelephonyEnabled.value && hasSipExtension.value
);

// SIP Connection
const {
  isConnected,
  isConnecting,
  connectionError,
  callState,
  callDirection,
  callNumber,
  remoteDisplayName,
  isMuted,
  isOnHold,
  hasActiveCall,
  isInCall,
  isConsultationActive,
  consultationState,
  consultationTarget,
  connect,
  makeCall: sipMakeCall,
  answerCall: sipAnswerCall,
  hangup,
  toggleMute,
  toggleHold: sipToggleHold,
  transfer: sipTransfer,
  attendedTransfer: sipAttendedTransfer,
  completeAttendedTransfer: sipCompleteAttendedTransfer,
  cancelAttendedTransfer: sipCancelAttendedTransfer,
  sendDTMF,
  getCallDuration,
  formatDuration,
  setCallMetadata,
  unlockAudio,
} = useAciSipConnection();

// Unlock audio on first user interaction (required by browsers)
const handleUserInteraction = () => {
  unlockAudio();
};

// Computed para chamada recebida
const isIncomingCall = computed(() => {
  const result =
    callState.value === 'ringing' && callDirection.value === 'incoming';
  return result;
});

// Contato ou agente do chamador
const callerContact = ref(null);
const callerAgent = ref(null);
const isLoadingCaller = ref(false);

// Flag para indicar se esta é uma chamada transferida (já completada)
// Quando recebemos uma chamada de um ramal interno, inicialmente é uma consulta
// Só após receber a notificação de transferência é que podemos transferir novamente
const isTransferReceived = ref(false);

// Agents from store (for extension lookup)
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Check if number is a known extension (agent with matching sip_extension)
const isExtensionNumber = number => {
  if (!number) return false;
  const clean = String(number).replace(/\D/g, '');
  return agents.value.some(a => String(a.sip_extension) === clean);
};

// Find agent by extension number
const findAgentByExtension = extensionNumber => {
  const clean = String(extensionNumber).replace(/\D/g, '');
  return agents.value.find(agent => {
    const agentExt = agent.sip_extension;
    return agentExt && String(agentExt) === clean;
  });
};

// Busca contato ou agente quando recebe chamada
let lookupRequestId = 0;
async function lookupCaller(phoneNumber) {
  if (!phoneNumber) {
    callerContact.value = null;
    callerAgent.value = null;
    return;
  }

  // Feature codes do Asterisk (*8000, #1, etc): não buscar contato — não é número de cliente
  if (/^[*#]/.test(String(phoneNumber))) {
    callerContact.value = null;
    callerAgent.value = null;
    return;
  }

  const requestId = ++lookupRequestId;
  isLoadingCaller.value = true;

  try {
    // Check if it's a known extension (internal call between agents)
    if (isExtensionNumber(phoneNumber)) {
      const agent = findAgentByExtension(phoneNumber);
      if (requestId !== lookupRequestId) return;
      if (agent) {
        callerAgent.value = agent;
        callerContact.value = null;
      } else {
        callerAgent.value = null;
        callerContact.value = null;
      }
      return;
    }

    // For external phone numbers, require at least 3 digits to search
    if (phoneNumber.length < 3) {
      callerContact.value = null;
      callerAgent.value = null;
      return;
    }

    // If not an extension or agent not found, search for contact
    const response = await ContactsAPI.lookupByPhone(phoneNumber);
    if (requestId !== lookupRequestId) return;

    if (response.data?.found && response.data?.payload) {
      callerContact.value = response.data.payload;
      callerAgent.value = null;
    } else {
      callerContact.value = null;
      callerAgent.value = null;
    }
  } catch (error) {
    if (requestId !== lookupRequestId) return;
    console.error('[DialerPanel] Error looking up caller:', error);
    callerContact.value = null;
    callerAgent.value = null;
  } finally {
    if (requestId === lookupRequestId) {
      isLoadingCaller.value = false;
    }
  }
}

// Watch para buscar chamador/destinatário quando recebe ou faz chamada
watch(
  [callNumber, callDirection],
  ([newNumber, newDirection]) => {
    if (newNumber && newDirection) {
      // Busca para chamadas recebidas e realizadas
      lookupCaller(newNumber);
    } else if (!newNumber) {
      callerContact.value = null;
      callerAgent.value = null;
    }
  },
  { immediate: true }
);

// Watch para atualizar metadados da chamada (para historico)
watch([callerContact, callerAgent], () => {
  const name = callerAgent.value?.name || callerContact.value?.name || null;
  const thumbnail =
    callerAgent.value?.thumbnail ||
    callerAgent.value?.avatar_url ||
    callerContact.value?.thumbnail ||
    null;
  if (name || thumbnail) {
    setCallMetadata({ name, thumbnail });
  }
});

// Nome do chamador (agente, contato ou número)
const callerDisplayName = computed(() => {
  return (
    callerAgent.value?.name ||
    callerContact.value?.name ||
    remoteDisplayName.value ||
    callNumber.value ||
    'Desconhecido'
  );
});

// Avatar/thumbnail do chamador
const callerThumbnail = computed(() => {
  if (callerAgent.value) {
    return callerAgent.value.thumbnail || callerAgent.value.avatar_url;
  }
  if (callerContact.value) {
    return callerContact.value.thumbnail;
  }
  return null;
});

// Controla se o botão de transferência deve ser exibido
// - Ocultar quando recebemos uma chamada de um ramal interno (consulta de transferência)
// - Mostrar após receber a notificação de que a transferência foi completada
// - Mostrar normalmente para chamadas externas ou chamadas que fizemos
const canShowTransferButton = computed(() => {
  // Se a chamada é de saída, sempre pode transferir
  if (callDirection.value === 'outgoing') {
    return true;
  }

  // Se a chamada é de entrada
  if (callDirection.value === 'incoming') {
    // Se é de um ramal interno (outro agente)
    if (callerAgent.value || isExtensionNumber(callNumber.value)) {
      // Só pode transferir se já recebeu a notificação de transferência
      return isTransferReceived.value;
    }
    // Se é de um número externo (cliente), pode transferir normalmente
    return true;
  }

  return true;
});

// Tabs
const activeTab = ref('dialpad');
const allTabs = [
  {
    id: 'dialpad',
    icon: 'i-lucide-phone',
    label: 'Telefone',
    activeColor: 'text-n-teal-11',
  },
  {
    id: 'extensions',
    icon: 'i-lucide-headphones',
    label: 'Ramais',
    activeColor: 'text-n-iris-11',
  },
  {
    id: 'queues',
    icon: 'i-lucide-users-round',
    label: 'Filas',
    activeColor: 'text-n-cyan-11',
  },
  {
    id: 'history',
    icon: 'i-lucide-history',
    label: 'Histórico',
    activeColor: 'text-n-amber-11',
  },
  {
    id: 'contacts',
    icon: 'i-lucide-users',
    label: 'Contatos',
    activeColor: 'text-n-blue-11',
  },
];
const tabs = computed(() =>
  hasActiveCall.value
    ? allTabs.filter(t => t.id !== 'extensions' && t.id !== 'queues')
    : allTabs
);

// Handle call state changes: always show dialpad and auto-expand
watch(hasActiveCall, active => {
  if (active) {
    activeTab.value = 'dialpad';
    if (isCompact.value) {
      isCompact.value = false;
    }
  }
});

// Extensions (Ramais)
const extensionsList = ref([]);
const isLoadingExtensions = ref(false);

// Map extension number to agent availability_status
const getExtensionAvailability = ext => {
  const extNumber = String(ext.extension);
  const agent = agents.value.find(a => String(a.sip_extension) === extNumber);
  return agent?.availability_status || 'offline';
};

const availabilityColor = ext => {
  const status = getExtensionAvailability(ext);
  if (status === 'online') return 'bg-n-teal-9';
  if (status === 'busy') return 'bg-n-amber-9';
  return 'bg-n-slate-9';
};

// Get current user's extension from custom_attributes.sip_extension
const currentUserExtension = computed(() => {
  const user = currentUser.value;
  const sipExt = user?.sip_extension;
  return sipExt ? String(sipExt) : null;
});

// Extensions search
const extensionSearch = ref('');

// Filter extensions to exclude current user's extension, apply search, and sort by name
const extensions = computed(() => {
  const myExtension = currentUserExtension.value;
  const query = extensionSearch.value.toLowerCase().trim();

  const filtered = extensionsList.value.filter(ext => {
    // Filter out inactive extensions
    const isActive =
      ext.active === 1 || ext.active === true || ext.active === '1';
    if (!isActive) return false;

    // Filter out current user's extension
    if (myExtension && String(ext.extension) === myExtension) return false;

    // Search filter
    if (query) {
      const name = (ext.caller_id_name || ext.display_name || '').toLowerCase();
      const number = String(ext.extension);
      if (!name.includes(query) && !number.includes(query)) return false;
    }

    return true;
  });

  // Sort by caller_id_name alphabetically (spread to avoid mutating filtered array)
  return [...filtered].sort((a, b) => {
    const nameA = (a.caller_id_name || a.display_name || '').toLowerCase();
    const nameB = (b.caller_id_name || b.display_name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

// Extensions available for transfer: only those linked to an agent (regardless of availability_status)
const transferExtensions = computed(() => {
  return extensions.value.filter(ext => findAgentByExtension(ext.extension));
});

const loadExtensions = async () => {
  isLoadingExtensions.value = true;
  try {
    const result = await store.dispatch('asteriskExtensions/fetch');
    extensionsList.value = result || [];
  } catch (error) {
    console.error('[DialerPanel] Error loading extensions:', error);
    extensionsList.value = [];
  } finally {
    isLoadingExtensions.value = false;
  }
};

// Queue Routes (Filas) — atalhos `#600` que roteiam pra filas Asterisk.
// Cliente nao quer digitar shortcode no dialpad, entao listamos como aba clicavel.
const queueRoutesList = ref([]);
const isLoadingQueueRoutes = ref(false);
const queueRouteSearch = ref('');

const filterQueueRoutes = rawQuery => {
  const query = rawQuery.toLowerCase().trim();
  const filtered = queueRoutesList.value.filter(route => {
    const isActive =
      route.active === 1 || route.active === true || route.active === '1';
    if (!isActive) return false;
    if (query) {
      const name = (route.queue_name || route.description || '').toLowerCase();
      const code = String(route.shortcode || '');
      if (!name.includes(query) && !code.includes(query)) return false;
    }
    return true;
  });
  return [...filtered].sort((a, b) => {
    const aCode = String(a.shortcode || '');
    const bCode = String(b.shortcode || '');
    return aCode.localeCompare(bCode);
  });
};

const queueRoutes = computed(() => filterQueueRoutes(queueRouteSearch.value));

const findQueueRouteByShortcode = code =>
  queueRoutesList.value.find(route => String(route.shortcode) === code);

const loadQueueRoutes = async () => {
  isLoadingQueueRoutes.value = true;
  try {
    const result = await store.dispatch('asteriskQueueRoutes/fetch');
    queueRoutesList.value = result || [];
  } catch (error) {
    console.error('[DialerPanel] Error loading queue routes:', error);
    queueRoutesList.value = [];
  } finally {
    isLoadingQueueRoutes.value = false;
  }
};

const callQueueRoute = async route => {
  if (!isConnected.value || isCallCooldown.value) return;
  // Shortcode tem 2-6 dígitos com '#' ou '*' OPCIONAL (shortcodeFormat no
  // QueueRouteForm: /^[#*]?\d{2,6}$/). O encode de '#' pra '%23' na SIP URI
  // acontece em AciSipClient.buildDialUri.
  phoneNumber.value = String(route.shortcode || '');
  selectedContact.value = null;
  activeTab.value = 'dialpad';
  await nextTick();
  if (!hasActiveCall.value && canCall.value) {
    handleMakeCall();
  }
};

const callExtension = async ext => {
  if (!isConnected.value || isCallCooldown.value) {
    return;
  }

  phoneNumber.value = String(ext.extension);
  selectedContact.value = null;
  activeTab.value = 'dialpad';

  await nextTick();

  if (!hasActiveCall.value && canCall.value) {
    handleMakeCall();
  } else {
  }
};

// ============================================
// Panel Controls: Drag, Compact
// ============================================
const panelRef = ref(null);
const isCompact = ref(false);
const isDragging = ref(false);

// Position (null = use default CSS position)
const position = ref({ x: null, y: null });

// Load saved preferences from localStorage
const loadPanelPreferences = () => {
  try {
    const saved = localStorage.getItem('dialer_panel_prefs');
    if (saved) {
      const prefs = JSON.parse(saved);
      if (prefs.position) position.value = prefs.position;
      if (prefs.isCompact !== undefined) isCompact.value = prefs.isCompact;
    }
  } catch (e) {
    console.error('[DialerPanel] Error loading preferences:', e);
  }
};

const savePanelPreferences = () => {
  try {
    localStorage.setItem(
      'dialer_panel_prefs',
      JSON.stringify({
        position: position.value,
        isCompact: isCompact.value,
      })
    );
  } catch (e) {
    console.error('[DialerPanel] Error saving preferences:', e);
  }
};

// Toggle compact mode
const toggleCompact = () => {
  isCompact.value = !isCompact.value;
  savePanelPreferences();
  // Reposition if needed after height change
  nextTick(() => constrainToViewport());
};

// Auto-close transfer modal when consultation ends unexpectedly
watch(isConsultationActive, (active, wasActive) => {
  if (
    !active &&
    wasActive &&
    showTransferModal.value &&
    transferMode.value === 'attended'
  ) {
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';
  }
});

// Drag functionality
let dragStartX = 0;
let dragStartY = 0;
let initialX = 0;
let initialY = 0;

const startDrag = e => {
  if (e.target.closest('button') || e.target.closest('input')) return;

  isDragging.value = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;

  // Get current position
  if (position.value.x === null) {
    const rect = panelRef.value?.getBoundingClientRect();
    if (rect) {
      initialX = rect.left;
      initialY = rect.top;
    }
  } else {
    initialX = position.value.x;
    initialY = position.value.y;
  }

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  e.preventDefault();
};

const onDrag = e => {
  if (!isDragging.value) return;

  const deltaX = e.clientX - dragStartX;
  const deltaY = e.clientY - dragStartY;

  let newX = initialX + deltaX;
  let newY = initialY + deltaY;

  // Constrain to viewport
  const panelWidth = PANEL_WIDTH;
  const panelHeight = isCompact.value
    ? PANEL_COMPACT_HEIGHT
    : PANEL_EXPANDED_HEIGHT;
  const maxX = window.innerWidth - panelWidth - VIEWPORT_PADDING;
  const maxY = window.innerHeight - panelHeight - VIEWPORT_PADDING;

  newX = Math.max(VIEWPORT_PADDING, Math.min(newX, maxX));
  newY = Math.max(VIEWPORT_PADDING, Math.min(newY, maxY));

  position.value = { x: newX, y: newY };
};

const stopDrag = () => {
  if (isDragging.value) {
    isDragging.value = false;
    savePanelPreferences();
  }
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

// Constrain panel position to viewport
const constrainToViewport = () => {
  if (position.value.x === null) return;

  const panelWidth = PANEL_WIDTH;
  const panelHeight = isCompact.value
    ? PANEL_COMPACT_HEIGHT
    : PANEL_EXPANDED_HEIGHT;
  const maxX = window.innerWidth - panelWidth - VIEWPORT_PADDING;
  const maxY = window.innerHeight - panelHeight - VIEWPORT_PADDING;

  let needsUpdate = false;
  let newX = position.value.x;
  let newY = position.value.y;

  if (newX > maxX) {
    newX = Math.max(VIEWPORT_PADDING, maxX);
    needsUpdate = true;
  }
  if (newY > maxY) {
    newY = Math.max(VIEWPORT_PADDING, maxY);
    needsUpdate = true;
  }
  if (newX < VIEWPORT_PADDING) {
    newX = VIEWPORT_PADDING;
    needsUpdate = true;
  }
  if (newY < VIEWPORT_PADDING) {
    newY = VIEWPORT_PADDING;
    needsUpdate = true;
  }

  if (needsUpdate) {
    position.value = { x: newX, y: newY };
  }
};

// Handle window resize
const handleWindowResize = () => {
  constrainToViewport();
};

// Panel style computed
const panelStyle = computed(() => {
  if (position.value.x === null) {
    return {};
  }
  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

// Local state
const phoneNumber = ref('');
const searchQuery = ref('');
const selectedContact = ref(null);

// Contacts from store
const contacts = computed(
  () => store.getters['contacts/getContactsList'] || []
);
const contactsUIFlags = computed(
  () => store.getters['contacts/getUIFlags'] || {}
);
const contactsMeta = computed(() => store.getters['contacts/getMeta'] || {});
const isSearching = computed(() => contactsUIFlags.value.isFetching);
const hasMoreContacts = computed(() => {
  const meta = contactsMeta.value;
  return meta?.currentPage < meta?.totalPages;
});
const callDuration = ref(0);

const showTransferModal = ref(false);
const transferTarget = ref('');
const transferMode = ref('blind'); // 'blind' | 'attended'
// Campo do modal: filtra ramais/filas e, se for discável, vira destino
// direto (ramal fora da lista, atalho de fila "#9029", número externo).
const transferQuery = ref('');

const transferQueryNumber = computed(() => {
  const value = transferQuery.value.replace(/[\s().-]/g, '');
  return /^[+*#]?\d[\d*#]*$/.test(value) ? value : '';
});

const transferExtensionsFiltered = computed(() => {
  const query = transferQuery.value.toLowerCase().trim();
  if (!query) return transferExtensions.value;
  return transferExtensions.value.filter(ext => {
    const name = (ext.caller_id_name || ext.display_name || '').toLowerCase();
    return name.includes(query) || String(ext.extension).includes(query);
  });
});

const transferQueueRoutes = computed(() =>
  filterQueueRoutes(transferQuery.value)
);

// Só oferece "discar o que foi digitado" quando não é exatamente um item já
// listado (aí o atendente clica no item, que tem nome).
const showTransferDialTyped = computed(() => {
  const typed = transferQueryNumber.value;
  if (!typed) return false;
  const isListedExtension = transferExtensions.value.some(
    ext => String(ext.extension) === typed
  );
  return !isListedExtension && !findQueueRouteByShortcode(typed);
});

// Fila antes do agente: findAgentByExtension descarta '#'/'*', então o atalho
// "#9029" casaria com o agente do ramal 9029.
const transferTargetName = target => {
  const route = findQueueRouteByShortcode(target);
  if (route) {
    return t('DIALER.TRANSFER.QUEUE_NAME', {
      name: route.description || route.queue_name || target,
    });
  }
  const agent = findAgentByExtension(target);
  return agent ? agent.name : target;
};

// Consultation target display (nome do agente ou da fila)
const consultationDisplayName = computed(() => {
  const target =
    consultationTarget.value ||
    (isConsultationActive.value ? transferTarget.value : null);
  if (!target) return null;
  return transferTargetName(target);
});

const consultationSubtitle = computed(() => {
  const target = consultationTarget.value || transferTarget.value;
  if (!target || !consultationDisplayName.value) return null;
  if (findQueueRouteByShortcode(target)) return target;
  if (consultationDisplayName.value === target) return null;
  return t('DIALER.TRANSFER.EXTENSION_LABEL', { extension: target });
});

watch(transferQuery, () => {
  transferTarget.value = '';
});
const isCompletingTransfer = ref(false);
const isCancellingConsultation = ref(false);
const contactsListRef = ref(null);
let durationTimer = null;
let callCooldownTimer = null;

// Cooldown after call ends (prevents ghost click during Vue re-render)
const isCallCooldown = ref(false);

// Loading states for call controls (prevent double-click)
const isTogglingMute = ref(false);
const isTogglingHold = ref(false);
const isTransferring = ref(false);
const isHangingUp = ref(false);
const isAnswering = ref(false);
const isRejecting = ref(false);

// Call history (compartilhado via composable)
const {
  callHistory,
  clearHistory: clearCallHistory,
  formatTimestamp: formatHistoryTime,
} = useCallHistory();

// Dial pad keys
const dialPadKeys = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

// Load call history from localStorage and initial contacts
onMounted(async () => {
  // Load saved panel preferences (position, size, compact state)
  loadPanelPreferences();

  // Validate position is within viewport after loading
  constrainToViewport();

  // If there's already an active call when mounting (e.g., incoming call opened the panel),
  // ensure we're on dialpad tab and expanded
  if (hasActiveCall.value) {
    activeTab.value = 'dialpad';
    isCompact.value = false;
  }

  // Listen for window resize to keep panel in bounds
  window.addEventListener('resize', handleWindowResize);

  // Listen for transfer completed events (when another agent transfers a call to us)
  emitter.on(BUS_EVENTS.TELEPHONY_TRANSFER_COMPLETED, handleTransferCompleted);

  // So conecta SIP se: feature ativo + agente tem ramal cadastrado.
  // Sem ramal, /asterisk/my_extension retorna 404 NO_EXTENSION_ASSIGNED.
  if (!canUseDialer.value) return;

  if (!isConnected.value && !isConnecting.value) {
    await connect();
  }
  // Call history is loaded automatically by useCallHistory composable
  loadInitialContacts();
  loadExtensions();
  loadQueueRoutes();
  // Load agents for caller ID lookup (internal calls)
  store.dispatch('agents/get');
});

// Load initial contacts sorted by last_activity_at
async function loadInitialContacts() {
  try {
    await store.dispatch('contacts/get', {
      page: 1,
      sortAttr: '-last_activity_at',
    });
  } catch (error) {
    console.error('Error loading contacts:', error);
  }
}

// Load more contacts on scroll
async function loadMoreContacts() {
  if (isSearching.value || !hasMoreContacts.value || searchQuery.value) return;

  const nextPage = (contactsMeta.value?.currentPage || 0) + 1;
  try {
    await store.dispatch('contacts/get', {
      page: nextPage,
      sortAttr: '-last_activity_at',
    });
  } catch (error) {
    console.error('Error loading more contacts:', error);
  }
}

// Handle scroll for infinite loading
function handleContactsScroll(event) {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;

  if (nearBottom && !searchQuery.value) {
    loadMoreContacts();
  }
}

// Handler para quando uma transferência é completada para este agente
const handleTransferCompleted = data => {
  // Verificar se a transferência é para este agente
  const myExtension = currentUserExtension.value;
  if (!myExtension || String(data.target_extension) !== String(myExtension)) {
    return;
  }

  // Marcar que a transferência foi recebida - agora pode transferir novamente
  isTransferReceived.value = true;

  // Atualizar as informações do chamador
  if (data.original_caller_number) {
    callerAgent.value = null;

    // Set temporary contact info while the real lookup resolves
    if (
      data.original_caller_name &&
      data.original_caller_name !== data.original_caller_number
    ) {
      callerContact.value = {
        name: data.original_caller_name,
        phone_number: data.original_caller_number,
      };
    } else {
      callerContact.value = null;
    }

    // Lookup will overwrite the temporary value when it resolves
    lookupCaller(data.original_caller_number);
  }
};

onUnmounted(() => {
  stopDurationTimer();
  clearTimeout(searchTimeout);
  clearTimeout(callCooldownTimer);
  window.removeEventListener('resize', handleWindowResize);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  emitter.off(BUS_EVENTS.TELEPHONY_TRANSFER_COMPLETED, handleTransferCompleted);
});

// Watch para controle do timer de duracao
// O historico agora e salvo automaticamente pelo useAciSipConnection
watch(callState, (newState, oldState) => {
  if (newState === 'connected') {
    startDurationTimer();
  } else if (newState === 'ended' || newState === 'idle') {
    stopDurationTimer();
    // Reset transfer flag when call ends
    isTransferReceived.value = false;

    // Clear dialpad and activate cooldown to prevent ghost re-dial from Vue re-render
    if (
      oldState === 'calling' ||
      oldState === 'ringing' ||
      oldState === 'connected' ||
      oldState === 'connecting'
    ) {
      phoneNumber.value = '';
      selectedContact.value = null;

      isCallCooldown.value = true;
      clearTimeout(callCooldownTimer);
      callCooldownTimer = setTimeout(() => {
        isCallCooldown.value = false;
      }, POST_CALL_COOLDOWN_MS);
    }
  }
});

// Call history functions are now provided by useCallHistory composable

function startDurationTimer() {
  stopDurationTimer();
  callDuration.value = 0;
  durationTimer = setInterval(() => {
    callDuration.value = getCallDuration();
  }, 1000);
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer);
    durationTimer = null;
  }
}

// Debounce search
let searchTimeout = null;
const debouncedSearch = query => {
  clearTimeout(searchTimeout);

  // If empty, reload initial contacts list
  if (!query || query.length < 2) {
    if (!query) {
      loadInitialContacts();
    }
    return;
  }

  searchTimeout = setTimeout(async () => {
    try {
      await store.dispatch('contacts/search', {
        search: query,
        page: 1,
        sortAttr: '-last_activity_at',
      });
    } catch (error) {
      console.error('Error searching contacts:', error);
    }
  }, DEBOUNCE_DELAY);
};

watch(searchQuery, newValue => {
  debouncedSearch(newValue);
});

const displayNumber = computed(() => {
  if (hasActiveCall.value && callNumber.value) {
    return callNumber.value;
  }
  if (selectedContact.value) {
    return (
      selectedContact.value.phoneNumber ||
      selectedContact.value.phone_number ||
      phoneNumber.value
    );
  }
  return phoneNumber.value;
});

const canCall = computed(() => {
  const num = displayNumber.value;
  return !!(
    isConnected.value &&
    !hasActiveCall.value &&
    num &&
    String(num).length >= 1
  );
});

const statusText = computed(() => {
  if (isConnecting.value) return t('DIALER.STATUS.CONNECTING');
  if (connectionError.value) return t('DIALER.STATUS.ERROR');
  if (!isConnected.value) return t('DIALER.STATUS.DISCONNECTED');

  switch (callState.value) {
    case 'calling':
      return t('DIALER.STATUS.CALLING');
    case 'ringing':
      return t('DIALER.STATUS.RINGING');
    case 'connecting':
      return t('DIALER.STATUS.CONNECTING');
    case 'connected':
      return formatDuration(callDuration.value);
    case 'ended':
      return t('DIALER.STATUS.ENDED');
    default:
      return t('DIALER.STATUS.READY');
  }
});

const statusColor = computed(() => {
  if (connectionError.value || !isConnected.value) return 'text-n-ruby-11';
  if (isConnecting.value) return 'text-n-amber-11';
  if (isInCall.value) return 'text-n-teal-11';
  return 'text-n-slate-11';
});

const pressKey = digit => {
  if (hasActiveCall.value) {
    sendDTMF(digit);
  } else {
    phoneNumber.value += digit;
    if (selectedContact.value) {
      selectedContact.value = null;
    }
  }
};

const deleteLastDigit = () => {
  if (!hasActiveCall.value) {
    phoneNumber.value = phoneNumber.value.slice(0, -1);
  }
};

const clearNumber = () => {
  phoneNumber.value = '';
  selectedContact.value = null;
  searchQuery.value = '';
};

const selectContact = async contact => {
  const contactPhone = contact.phoneNumber || contact.phone_number || '';
  if (!contactPhone) {
    useAlert('Contato sem número de telefone');
    return;
  }

  selectedContact.value = contact;
  phoneNumber.value = contactPhone;
  searchQuery.value = '';
  activeTab.value = 'dialpad';

  // Aguarda atualização dos computeds e liga diretamente
  await nextTick();
  if (isConnected.value && !hasActiveCall.value && canCall.value) {
    handleMakeCall();
  }
};

const callFromHistory = async item => {
  phoneNumber.value = item.number;
  selectedContact.value = null;
  activeTab.value = 'dialpad';

  // Aguarda atualização dos computeds e liga diretamente
  await nextTick();
  if (isConnected.value && !hasActiveCall.value && canCall.value) {
    handleMakeCall();
  }
};

const handleMakeCall = () => {
  if (!canCall.value || isCallCooldown.value) {
    return;
  }

  const numberToCall = displayNumber.value;

  try {
    sipMakeCall(numberToCall, event => {
      if (event.type === 'failed') {
        useAlert(t('DIALER.CALL_FAILED'));
      }
    });
  } catch (err) {
    console.error('[DialerPanel] Error making call:', err);
    useAlert(err.message);
  }
};

const handleHangup = async () => {
  if (isHangingUp.value) return;
  isHangingUp.value = true;
  try {
    await hangup();
  } finally {
    setTimeout(() => {
      isHangingUp.value = false;
    }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleAnswerCall = async () => {
  if (isAnswering.value) return;
  isAnswering.value = true;

  try {
    await sipAnswerCall();
  } catch (err) {
    console.error('[DialerPanel] Error answering call:', err);
    useAlert(err.message);
  } finally {
    setTimeout(() => {
      isAnswering.value = false;
    }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleRejectCall = async () => {
  if (isRejecting.value) return;
  isRejecting.value = true;

  try {
    await hangup();
  } catch (err) {
    console.error('[DialerPanel] Error rejecting call:', err);
  } finally {
    setTimeout(() => {
      isRejecting.value = false;
    }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleToggleMute = async () => {
  if (isTogglingMute.value) return;
  isTogglingMute.value = true;
  try {
    await toggleMute();
  } finally {
    setTimeout(() => {
      isTogglingMute.value = false;
    }, SHORT_GUARD_MS);
  }
};

const handleToggleHold = async () => {
  if (isTogglingHold.value) return;
  isTogglingHold.value = true;
  try {
    const wasOnHold = isOnHold.value;
    await sipToggleHold();
    useAlert(!wasOnHold ? 'Chamada em espera' : 'Chamada retomada');
  } catch (err) {
    console.error('[DialerPanel] Hold error:', err);
    useAlert(err.message);
  } finally {
    setTimeout(() => {
      isTogglingHold.value = false;
    }, SHORT_GUARD_MS);
  }
};

const handleTransfer = () => {
  transferQuery.value = '';
  transferTarget.value = '';
  transferMode.value = 'blind';
  showTransferModal.value = true;
};

const confirmTransfer = async () => {
  if (!transferTarget.value || isTransferring.value) return;
  isTransferring.value = true;

  // Save info before clearing (needed for notification)
  const targetExtension = transferTarget.value;
  const originalCallerNumber = callNumber.value;
  const originalCallerName =
    callerContact.value?.name || callerAgent.value?.name || null;
  const transferorExtension = currentUserExtension.value;
  const transferorName = currentUser.value?.name || null;

  const targetDisplayName = transferTargetName(targetExtension);
  // Fila não tem atendente definido ainda — ninguém a notificar.
  const shouldNotifyTarget = !findQueueRouteByShortcode(targetExtension);

  try {
    if (transferMode.value === 'blind') {
      // Blind transfer (existing behavior)
      await sipTransfer(targetExtension);
      useAlert(`Transferindo para ${targetDisplayName}...`);
      showTransferModal.value = false;
      transferTarget.value = '';
      transferMode.value = 'blind';

      // Notify the target agent about the transfer with original caller info
      if (shouldNotifyTarget) {
        try {
          await TransferNotificationsAPI.create({
            target_extension: targetExtension,
            original_caller_number: originalCallerNumber,
            original_caller_name: originalCallerName,
            transferor_extension: transferorExtension,
            transferor_name: transferorName,
          });
        } catch (notifyErr) {
          // Non-blocking - notification failure is not critical
        }
      }
    } else {
      // Attended transfer: start consultation
      await sipAttendedTransfer(targetExtension);
      // Modal stays open and switches to consultation phase
    }
  } catch (err) {
    console.error('[DialerPanel] Transfer error:', err);
    useAlert(err.message);
  } finally {
    setTimeout(() => {
      isTransferring.value = false;
    }, SHORT_GUARD_MS);
  }
};

const handleTransferQueryEnter = () => {
  if (!transferQueryNumber.value) return;
  transferTarget.value = transferQueryNumber.value;
  confirmTransfer();
};

const handleCompleteAttendedTransfer = async () => {
  if (isCompletingTransfer.value) return;
  isCompletingTransfer.value = true;

  // Save info before clearing (needed for notification)
  const targetExtension = transferTarget.value;
  const originalCallerNumber = callNumber.value;
  const originalCallerName =
    callerContact.value?.name || callerAgent.value?.name || null;
  const transferorExtension = currentUserExtension.value;
  const transferorName = currentUser.value?.name || null;

  const targetDisplayName = transferTargetName(targetExtension);
  // Fila não tem atendente definido ainda — ninguém a notificar.
  const shouldNotifyTarget = !findQueueRouteByShortcode(targetExtension);

  try {
    await sipCompleteAttendedTransfer();
    useAlert(`Transferência para ${targetDisplayName} completada`);
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';

    // Notify the target agent about the transfer with original caller info
    if (shouldNotifyTarget) {
      try {
        await TransferNotificationsAPI.create({
          target_extension: targetExtension,
          original_caller_number: originalCallerNumber,
          original_caller_name: originalCallerName,
          transferor_extension: transferorExtension,
          transferor_name: transferorName,
        });
      } catch (notifyErr) {
        // Non-blocking - notification failure is not critical
      }
    }
  } catch (err) {
    console.error('[DialerPanel] Complete attended transfer error:', err);
    useAlert(err.message);
  } finally {
    setTimeout(() => {
      isCompletingTransfer.value = false;
    }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleCancelConsultation = async () => {
  if (isCancellingConsultation.value) return;
  isCancellingConsultation.value = true;

  try {
    await sipCancelAttendedTransfer();
    useAlert('Consulta cancelada, retornando à chamada original');
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';
  } catch (err) {
    console.error('[DialerPanel] Cancel consultation error:', err);
    useAlert(err.message);
  } finally {
    setTimeout(() => {
      isCancellingConsultation.value = false;
    }, DOUBLE_CLICK_GUARD_MS);
  }
};

const getContactPhone = contact => {
  return (
    contact.phoneNumber ||
    contact.phone_number ||
    contact.additionalAttributes?.phone ||
    contact.additional_attributes?.phone ||
    '-'
  );
};

// formatHistoryTime and clearCallHistory are now provided by useCallHistory composable
</script>

<template>
  <div
    ref="panelRef"
    class="fixed z-50"
    :class="position.x === null ? 'bottom-4 ltr:right-4 rtl:left-4' : ''"
    :style="panelStyle"
    @click.capture="handleUserInteraction"
  >
    <!-- Altura expandida amarrada em PANEL_EXPANDED_HEIGHT — a MESMA constante
         do clamp de arrasto. Style dinâmico ligado a constante JS (como o
         panelStyle da posição), não o inline style estático que a regra do
         repo proíbe: a classe h-[530px] duplicada já divergiu da constante e
         cortou o botão "Ligar" em silêncio. -->
    <div
      class="bg-n-solid-2 rounded-2xl shadow-2xl overflow-hidden border border-n-weak flex flex-col relative w-[280px]"
      :class="{ 'select-none': isDragging }"
      :style="isCompact ? null : { height: `${PANEL_EXPANDED_HEIGHT}px` }"
    >
      <!-- Header (Draggable) -->
      <div
        class="flex items-center justify-between px-3 py-2 border-b cursor-move"
        :class="{
          'cursor-grabbing': isDragging,
          'border-b-0': isCompact && !hasActiveCall,
          'bg-n-amber-3 dark:bg-n-amber-9/20 border-n-amber-6':
            hasActiveCall &&
            (callState === 'calling' || callState === 'ringing'),
          'bg-n-teal-3 dark:bg-n-teal-9/20 border-n-teal-6':
            hasActiveCall &&
            (callState === 'connecting' || callState === 'connected'),
          'bg-n-solid-3 border-n-weak': !hasActiveCall,
        }"
        @mousedown="startDrag"
      >
        <!-- Active call: show call info in header -->
        <div
          v-if="hasActiveCall"
          class="flex items-center gap-2 flex-1 min-w-0"
        >
          <span
            class="size-2 rounded-full animate-pulse flex-shrink-0"
            :class="{
              'bg-n-amber-9':
                callState === 'calling' || callState === 'ringing',
              'bg-n-teal-9':
                callState === 'connecting' || callState === 'connected',
            }"
          />
          <p
            class="text-sm truncate"
            :class="{
              'text-n-amber-11':
                callState === 'calling' || callState === 'ringing',
              'text-n-teal-11':
                callState === 'connecting' || callState === 'connected',
            }"
          >
            <span class="font-semibold">{{ statusText }}</span>
            <span
              v-if="
                selectedContact ||
                callNumber ||
                callerAgent ||
                callerContact ||
                remoteDisplayName
              "
              class="font-normal"
            >
              ·
              {{
                callerAgent?.name ||
                callerContact?.name ||
                selectedContact?.name ||
                remoteDisplayName ||
                callNumber
              }}</span>
          </p>
        </div>

        <!-- Normal header: spacer -->
        <div v-else class="flex-1" />

        <div class="flex items-center gap-1">
          <!-- Compact/Expand Button (only show during calls) -->
          <Button
            v-if="hasActiveCall"
            v-tooltip="isCompact ? 'Expandir' : 'Minimizar'"
            :icon="isCompact ? 'i-lucide-maximize-2' : 'i-lucide-minimize-2'"
            variant="ghost"
            color="slate"
            size="xs"
            @click.stop="toggleCompact"
          />
          <!-- Close Button -->
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="xs"
            @click.stop="emit('close')"
          />
        </div>
      </div>

      <!-- Content (hidden when compact) -->
      <template v-if="!isCompact">
        <!-- Tabs -->
        <div class="flex items-center gap-1 px-2 py-1.5 border-b border-n-weak">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            v-tooltip="tab.label"
            class="flex-1 flex items-center justify-center py-1.5 rounded-md transition-all duration-200"
            :class="
              activeTab === tab.id
                ? ['bg-n-alpha-2 dark:bg-n-solid-3 shadow-sm', tab.activeColor]
                : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="activeTab = tab.id"
          >
            <span class="size-4" :class="[tab.icon]" />
          </button>
        </div>

        <DialerPauseBar :is-connected="isConnected" />

        <!-- ✅ CORREÇÃO: Remover altura fixa e usar flex-1 para ocupar espaço disponível -->
        <div class="flex-1 overflow-hidden flex flex-col min-h-0">
          <!-- Dialpad Tab -->
          <div
            v-if="activeTab === 'dialpad'"
            class="flex-1 flex flex-col min-h-0"
          >
            <!-- Incoming Call UI -->
            <div
              v-if="isIncomingCall"
              class="flex-1 flex flex-col items-center justify-center px-4 py-6 bg-gradient-to-b from-n-solid-3 to-n-solid-2"
            >
              <div class="flex flex-col items-center gap-4 mb-8">
                <div class="relative">
                  <!-- Avatar do agente, contato ou ícone de telefone -->
                  <div
                    v-if="callerAgent || callerContact"
                    class="w-20 h-20 rounded-full bg-n-teal-3 flex items-center justify-center animate-pulse overflow-hidden"
                  >
                    <Avatar
                      :src="callerThumbnail"
                      :name="callerDisplayName"
                      :size="80"
                      rounded-full
                    />
                  </div>
                  <div
                    v-else
                    class="w-20 h-20 rounded-full bg-n-teal-3 flex items-center justify-center animate-pulse"
                  >
                    <span
                      class="i-lucide-phone-incoming size-10 text-n-teal-11"
                    />
                  </div>
                  <div
                    class="absolute -inset-2 rounded-full border-2 border-n-teal-6 animate-ping opacity-50"
                  />
                </div>
                <div class="text-center">
                  <p class="text-sm text-n-slate-10 mb-1">Chamada recebida</p>
                  <p class="text-xl font-semibold text-n-slate-12">
                    {{ callerDisplayName }}
                  </p>
                  <p
                    v-if="callerAgent || callerContact"
                    class="text-sm text-n-slate-10 mt-0.5"
                  >
                    {{ callerAgent ? `Ramal ${callNumber}` : callNumber }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-6">
                <button
                  class="flex flex-col items-center gap-2 group"
                  :disabled="isRejecting"
                  :class="{ 'opacity-50 pointer-events-none': isRejecting }"
                  @click="handleRejectCall"
                >
                  <div
                    class="w-16 h-16 rounded-full bg-n-ruby-9 hover:bg-n-ruby-10 flex items-center justify-center transition-colors shadow-lg"
                  >
                    <span
                      v-if="!isRejecting"
                      class="i-lucide-phone-off size-7 text-white"
                    />
                    <span
                      v-else
                      class="i-lucide-loader-2 size-7 text-white animate-spin"
                    />
                  </div>
                  <span
                    class="text-xs text-n-slate-11 group-hover:text-n-slate-12"
                    >Recusar</span>
                </button>
                <button
                  class="flex flex-col items-center gap-2 group"
                  :disabled="isAnswering"
                  :class="{ 'opacity-50 pointer-events-none': isAnswering }"
                  @click="handleAnswerCall"
                >
                  <div
                    class="w-16 h-16 rounded-full bg-n-teal-9 hover:bg-n-teal-10 flex items-center justify-center transition-colors shadow-lg"
                    :class="{ 'animate-bounce': !isAnswering }"
                  >
                    <span
                      v-if="!isAnswering"
                      class="i-lucide-phone size-7 text-white"
                    />
                    <span
                      v-else
                      class="i-lucide-loader-2 size-7 text-white animate-spin"
                    />
                  </div>
                  <span
                    class="text-xs text-n-slate-11 group-hover:text-n-slate-12"
                    >Atender</span>
                </button>
              </div>
            </div>

            <!-- Normal Dialpad UI (when not incoming call) -->
            <template v-else>
              <!-- Number Display -->
              <div
                class="px-2 py-2 flex-shrink-0 flex items-center justify-center"
              >
                <div
                  class="flex items-center gap-2 w-full max-w-[240px] bg-n-alpha-2 dark:bg-n-solid-1 rounded-lg px-3 py-2 border border-n-weak"
                >
                  <div class="flex-1 min-w-0">
                    <p
                      v-if="selectedContact && !hasActiveCall"
                      class="text-[10px] text-n-slate-10 truncate text-center"
                    >
                      {{ selectedContact.name }}
                    </p>
                    <input
                      v-model="phoneNumber"
                      type="tel"
                      :placeholder="t('DIALER.NUMBER_PLACEHOLDER')"
                      class="w-full text-xl font-semibold text-n-slate-12 bg-transparent border-none focus:outline-none text-center no-margin"
                      :readonly="hasActiveCall"
                    />
                  </div>
                  <button
                    v-if="phoneNumber && !hasActiveCall"
                    class="p-1 rounded hover:bg-n-alpha-2 transition-colors"
                    @click="deleteLastDigit"
                    @mousedown.prevent
                  >
                    <span
                      class="i-lucide-delete size-5 text-n-slate-10 hover:text-n-slate-12"
                    />
                  </button>
                </div>
              </div>

              <!-- Dial Pad -->
              <div
                class="px-2 flex-shrink-0 flex items-center justify-center"
                :class="hasActiveCall ? 'py-2' : 'py-3'"
              >
                <div class="grid grid-cols-3 gap-2 w-full max-w-[240px]">
                  <button
                    v-for="key in dialPadKeys"
                    :key="key.digit"
                    class="flex flex-col items-center justify-center h-11 rounded-lg bg-n-solid-3 hover:bg-n-alpha-2 active:bg-n-alpha-3 transition-colors"
                    @click="pressKey(key.digit)"
                  >
                    <span class="text-lg font-medium text-n-slate-12">{{
                      key.digit
                    }}</span>
                    <span
                      v-if="key.letters"
                      class="text-[9px] text-n-slate-10 tracking-wider"
                    >
                      {{ key.letters }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Action Buttons (quando não está em chamada) -->
              <div
                v-if="!hasActiveCall"
                class="px-2 py-1.5 border-t border-n-weak flex-shrink-0 flex justify-center"
              >
                <div class="flex items-center gap-1.5 w-full max-w-[240px]">
                  <Button
                    v-if="phoneNumber"
                    icon="i-lucide-x"
                    variant="ghost"
                    color="slate"
                    class="flex-shrink-0"
                    @click="clearNumber"
                  />
                  <Button
                    icon="i-lucide-phone"
                    color="teal"
                    class="flex-1"
                    :disabled="!canCall"
                    :is-loading="isConnecting"
                    @click="handleMakeCall"
                  >
                    {{ t('DIALER.CALL') }}
                  </Button>
                </div>
              </div>

              <!-- In-Call Controls (quando está em chamada) -->
              <div
                v-if="hasActiveCall && !isIncomingCall"
                class="px-2 py-2 border-t border-n-weak flex-shrink-0 flex justify-center"
              >
                <div
                  class="flex items-center justify-center gap-3 w-full max-w-[240px]"
                >
                  <!-- Mute -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isTogglingMute"
                    :class="{
                      'opacity-50 pointer-events-none': isTogglingMute,
                    }"
                    @click="handleToggleMute"
                  >
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      :class="
                        isMuted
                          ? 'bg-n-ruby-9 hover:bg-n-ruby-10'
                          : 'bg-n-solid-3 hover:bg-n-alpha-2'
                      "
                    >
                      <span
                        v-if="!isTogglingMute"
                        :class="
                          isMuted
                            ? 'i-lucide-mic-off text-white'
                            : 'i-lucide-mic text-n-slate-11'
                        "
                        class="size-5"
                      />
                      <span
                        v-else
                        class="i-lucide-loader-2 size-5 text-n-slate-11 animate-spin"
                      />
                    </div>
                    <span class="text-[10px] text-n-slate-10">{{
                      isMuted ? 'Ativar' : 'Mudo'
                    }}</span>
                  </button>

                  <!-- Hold -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isTogglingHold"
                    :class="{
                      'opacity-50 pointer-events-none': isTogglingHold,
                    }"
                    @click="handleToggleHold"
                  >
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      :class="
                        isOnHold
                          ? 'bg-n-amber-9 hover:bg-n-amber-10'
                          : 'bg-n-solid-3 hover:bg-n-alpha-2'
                      "
                    >
                      <span
                        v-if="!isTogglingHold"
                        :class="
                          isOnHold
                            ? 'i-lucide-play text-white'
                            : 'i-lucide-pause text-n-slate-11'
                        "
                        class="size-5"
                      />
                      <span
                        v-else
                        class="i-lucide-loader-2 size-5 text-n-slate-11 animate-spin"
                      />
                    </div>
                    <span class="text-[10px] text-n-slate-10">{{
                      isOnHold ? 'Retomar' : 'Espera'
                    }}</span>
                  </button>

                  <!-- Transfer (hidden during consultation call, shown after transfer completed) -->
                  <button
                    v-if="canShowTransferButton"
                    class="flex flex-col items-center gap-1 group"
                    @click="handleTransfer"
                  >
                    <div
                      class="w-10 h-10 rounded-full bg-n-solid-3 hover:bg-n-alpha-2 flex items-center justify-center transition-colors"
                    >
                      <span
                        class="i-lucide-phone-forwarded size-5 text-n-slate-11"
                      />
                    </div>
                    <span class="text-[10px] text-n-slate-10">Transferir</span>
                  </button>

                  <!-- Hangup -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isHangingUp"
                    :class="{ 'opacity-50 pointer-events-none': isHangingUp }"
                    @click="handleHangup"
                  >
                    <div
                      class="w-10 h-10 rounded-full bg-n-ruby-9 hover:bg-n-ruby-10 flex items-center justify-center transition-colors"
                    >
                      <span
                        v-if="!isHangingUp"
                        class="i-lucide-phone-off size-5 text-white"
                      />
                      <span
                        v-else
                        class="i-lucide-loader-2 size-5 text-white animate-spin"
                      />
                    </div>
                    <span class="text-[10px] text-n-slate-10">Desligar</span>
                  </button>
                </div>
              </div>
            </template>
          </div>

          <!-- Extensions Tab (Ramais) -->
          <div
            v-else-if="activeTab === 'extensions'"
            class="flex-1 flex flex-col min-h-0"
          >
            <div
              v-if="isLoadingExtensions && extensionsList.length === 0"
              class="flex items-center justify-center py-8"
            >
              <Spinner size="small" />
            </div>
            <div
              v-else-if="extensionsList.length === 0"
              class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10"
            >
              <span class="i-lucide-headphones size-12 mb-2 opacity-50" />
              <p class="text-sm">Nenhum ramal disponível</p>
            </div>
            <div v-else class="flex-1 flex flex-col min-h-0">
              <div
                class="px-3 py-2 border-b border-n-weak flex-shrink-0 space-y-2"
              >
                <div class="flex items-center justify-between">
                  <span class="text-xs text-n-slate-10 font-medium">Ramais Disponíveis ({{ extensions.length }})</span>
                  <button
                    class="text-xs text-woot-600 dark:text-woot-400 hover:underline flex items-center gap-1"
                    :disabled="isLoadingExtensions"
                    @click="loadExtensions"
                  >
                    <span
                      class="i-lucide-refresh-cw size-3"
                      :class="isLoadingExtensions ? 'animate-spin' : ''"
                    />
                    Atualizar
                  </button>
                </div>
                <input
                  v-model="extensionSearch"
                  type="text"
                  placeholder="Buscar por nome ou ramal..."
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-transparent"
                />
              </div>
              <div
                v-if="transferExtensions.length > 0"
                class="flex-1 overflow-y-auto"
              >
                <div class="p-2">
                  <button
                    v-for="ext in transferExtensions"
                    :key="ext.id || ext.extension"
                    v-tooltip="
                      !isConnected ? t('DIALER.STATUS.DISCONNECTED') : ''
                    "
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left"
                    :class="
                      isConnected
                        ? 'hover:bg-n-alpha-1 cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                    "
                    :disabled="!isConnected"
                    @click="isConnected && callExtension(ext)"
                  >
                    <div
                      class="relative flex items-center justify-center w-9 h-9 rounded-full bg-n-teal-3 dark:bg-n-teal-9/20"
                    >
                      <span class="i-lucide-headphones size-4 text-n-teal-11" />
                      <span
                        class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-white dark:border-n-solid-3"
                        :class="availabilityColor(ext)"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium truncate text-n-slate-12">
                        {{
                          ext.caller_id_name ||
                          ext.display_name ||
                          'Ramal ' + ext.extension
                        }}
                      </p>
                      <p class="text-xs text-n-slate-10">
                        Ramal {{ ext.extension }}
                      </p>
                    </div>
                    <span
                      class="i-lucide-phone size-4"
                      :class="
                        isConnected ? 'text-n-teal-11' : 'text-n-slate-10'
                      "
                    />
                  </button>
                </div>
              </div>
              <div
                v-else
                class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <span class="i-lucide-search size-8 mb-2 opacity-50" />
                <p class="text-sm">Nenhum ramal encontrado</p>
              </div>
            </div>
          </div>

          <!-- Queue Routes Tab (Filas) -->
          <div
            v-else-if="activeTab === 'queues'"
            class="flex-1 flex flex-col min-h-0"
          >
            <div
              v-if="isLoadingQueueRoutes && queueRoutesList.length === 0"
              class="flex items-center justify-center py-8"
            >
              <Spinner size="small" />
            </div>
            <div
              v-else-if="queueRoutesList.length === 0"
              class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10"
            >
              <span class="i-lucide-users-round size-12 mb-2 opacity-50" />
              <p class="text-sm">Nenhuma fila disponível</p>
            </div>
            <div v-else class="flex-1 flex flex-col min-h-0">
              <div
                class="px-3 py-2 border-b border-n-weak flex-shrink-0 space-y-2"
              >
                <div class="flex items-center justify-between">
                  <span class="text-xs text-n-slate-10 font-medium">Filas ({{ queueRoutes.length }})</span>
                  <button
                    class="text-xs text-woot-600 dark:text-woot-400 hover:underline flex items-center gap-1"
                    :disabled="isLoadingQueueRoutes"
                    @click="loadQueueRoutes"
                  >
                    <span
                      class="i-lucide-refresh-cw size-3"
                      :class="isLoadingQueueRoutes ? 'animate-spin' : ''"
                    />
                    Atualizar
                  </button>
                </div>
                <input
                  v-model="queueRouteSearch"
                  type="text"
                  placeholder="Buscar por nome ou código..."
                  class="w-full !px-3 !py-1.5 !m-0 text-xs rounded-md border border-solid border-n-weak bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:!outline-none focus:ring-1 focus:ring-woot-500"
                />
              </div>
              <div v-if="queueRoutes.length > 0" class="flex-1 overflow-y-auto">
                <div class="p-2">
                  <button
                    v-for="route in queueRoutes"
                    :key="route.id || route.shortcode"
                    v-tooltip="
                      !isConnected ? t('DIALER.STATUS.DISCONNECTED') : ''
                    "
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left"
                    :class="
                      isConnected
                        ? 'hover:bg-n-alpha-1 cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                    "
                    :disabled="!isConnected"
                    @click="isConnected && callQueueRoute(route)"
                  >
                    <div
                      class="flex items-center justify-center w-9 h-9 rounded-full bg-n-cyan-3 dark:bg-n-cyan-9/20"
                    >
                      <span
                        class="i-lucide-users-round size-4 text-n-cyan-11"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium truncate text-n-slate-12">
                        {{
                          route.queue_name ||
                          route.description ||
                          'Fila ' + route.shortcode
                        }}
                      </p>
                      <p class="text-xs text-n-slate-10">
                        {{ route.shortcode
                        }}<span v-if="route.description && route.queue_name">
                          · {{ route.description }}</span>
                      </p>
                    </div>
                    <span
                      class="i-lucide-phone size-4"
                      :class="
                        isConnected ? 'text-n-teal-11' : 'text-n-slate-10'
                      "
                    />
                  </button>
                </div>
              </div>
              <div
                v-else
                class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <span class="i-lucide-search size-8 mb-2 opacity-50" />
                <p class="text-sm">Nenhuma fila encontrada</p>
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div
            v-else-if="activeTab === 'history'"
            class="flex-1 flex flex-col min-h-0"
          >
            <div
              v-if="callHistory.length === 0"
              class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10"
            >
              <span class="i-lucide-history size-12 mb-2 opacity-50" />
              <p class="text-sm">Nenhuma chamada recente</p>
            </div>
            <div v-else class="flex-1 flex flex-col min-h-0">
              <div
                class="flex items-center justify-between px-4 py-2 border-b border-n-weak flex-shrink-0"
              >
                <span class="text-xs text-n-slate-10 font-medium">Chamadas Recentes</span>
                <button
                  class="text-xs text-n-ruby-11 hover:text-n-ruby-9"
                  @click="clearCallHistory"
                >
                  Limpar
                </button>
              </div>
              <div class="flex-1 overflow-y-auto">
                <div class="p-2">
                  <button
                    v-for="item in callHistory"
                    :key="item.id"
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-n-alpha-1 transition-colors text-left"
                    @click="callFromHistory(item)"
                  >
                    <div v-if="item.name" class="relative flex-shrink-0">
                      <Avatar
                        :src="item.thumbnail"
                        :name="item.name"
                        :size="36"
                        rounded-full
                      />
                      <span
                        class="absolute -bottom-0.5 -right-0.5 size-4 rounded-full bg-n-solid-2 flex items-center justify-center"
                      >
                        <span
                          :class="
                            item.type === 'outgoing'
                              ? 'i-lucide-phone-outgoing'
                              : 'i-lucide-phone-incoming'
                          "
                          class="size-2.5 text-n-slate-10"
                        />
                      </span>
                    </div>
                    <div
                      v-else
                      class="flex items-center justify-center w-9 h-9 rounded-full bg-n-solid-3 flex-shrink-0"
                    >
                      <span
                        :class="
                          item.type === 'outgoing'
                            ? 'i-lucide-phone-outgoing'
                            : 'i-lucide-phone-incoming'
                        "
                        class="size-4 text-n-slate-11"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-n-slate-12 truncate">
                        {{ item.name || item.number }}
                      </p>
                      <p class="text-xs text-n-slate-10 truncate">
                        <span v-if="item.name">{{ item.number }} · </span>
                        {{ formatHistoryTime(item.timestamp) }}
                        <span v-if="item.duration">
                          · {{ formatDuration(item.duration) }}</span>
                      </p>
                    </div>
                    <span class="i-lucide-phone size-4 text-n-teal-11" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div
            v-else-if="activeTab === 'contacts'"
            class="flex-1 flex flex-col min-h-0"
          >
            <!-- Search -->
            <div class="p-3 border-b border-n-weak flex-shrink-0">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('DIALER.SEARCH_PLACEHOLDER')"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              />
            </div>

            <!-- Results -->
            <div
              ref="contactsListRef"
              class="flex-1 overflow-y-auto"
              @scroll="handleContactsScroll"
            >
              <div
                v-if="isSearching && contacts.length === 0"
                class="flex items-center justify-center py-8"
              >
                <Spinner size="small" />
              </div>
              <div
                v-else-if="contacts.length === 0 && searchQuery.length >= 2"
                class="flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <span class="i-lucide-user-x size-12 mb-2 opacity-50" />
                <p class="text-sm">{{ t('DIALER.NO_CONTACTS') }}</p>
              </div>
              <div
                v-else-if="contacts.length === 0 && !isSearching"
                class="flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <span class="i-lucide-users size-12 mb-2 opacity-50" />
                <p class="text-sm">Nenhum contato encontrado</p>
              </div>
              <div v-else class="p-2">
                <button
                  v-for="contact in contacts"
                  :key="contact.id"
                  class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-n-alpha-1 transition-colors text-left"
                  @click="selectContact(contact)"
                >
                  <Avatar
                    :src="contact.thumbnail"
                    :name="contact.name"
                    :size="36"
                    rounded-full
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ contact.name }}
                    </p>
                    <p class="text-xs text-n-slate-10 truncate">
                      {{ getContactPhone(contact) }}
                    </p>
                  </div>
                  <span class="i-lucide-phone size-4 text-n-teal-11" />
                </button>
                <!-- Loading more indicator -->
                <div
                  v-if="isSearching && contacts.length > 0"
                  class="flex items-center justify-center py-4"
                >
                  <Spinner size="small" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Transfer Modal -->
      <div
        v-if="showTransferModal"
        class="absolute inset-0 bg-n-alpha-black2 flex flex-col"
      >
        <div class="bg-n-solid-2 flex flex-col h-full">
          <!-- ===== PHASE 1: Select destination & mode (no consultation active) ===== -->
          <template v-if="!isConsultationActive">
            <!-- Header -->
            <div
              class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0"
            >
              <h4 class="text-sm font-semibold text-n-slate-12">
                Transferir Chamada
              </h4>
              <Button
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="xs"
                @click="
                  showTransferModal = false;
                  transferMode = 'blind';
                "
              />
            </div>

            <!-- Transfer mode toggle -->
            <div class="px-4 pt-3 pb-1 flex-shrink-0">
              <div class="flex rounded-lg border border-n-weak overflow-hidden">
                <button
                  class="flex-1 py-1.5 text-xs font-medium text-center transition-colors"
                  :class="
                    transferMode === 'blind'
                      ? 'bg-n-teal-3 dark:bg-n-teal-9/20 text-n-teal-11 border-r border-n-weak'
                      : 'bg-n-solid-3 text-n-slate-10 hover:text-n-slate-12 border-r border-n-weak'
                  "
                  @click="transferMode = 'blind'"
                >
                  Direta
                </button>
                <button
                  class="flex-1 py-1.5 text-xs font-medium text-center transition-colors"
                  :class="
                    transferMode === 'attended'
                      ? 'bg-n-teal-3 dark:bg-n-teal-9/20 text-n-teal-11'
                      : 'bg-n-solid-3 text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="transferMode = 'attended'"
                >
                  Assistida
                </button>
              </div>
              <p class="text-[10px] text-n-slate-10 mt-1.5 px-0.5">
                {{
                  transferMode === 'blind'
                    ? 'Transfere diretamente sem consulta.'
                    : 'Consulta o destino antes de transferir.'
                }}
              </p>
            </div>

            <!-- Destino digitado: filtra a lista ou disca direto -->
            <div class="px-4 pt-2 pb-1 flex-shrink-0">
              <input
                v-model="transferQuery"
                type="text"
                :placeholder="t('DIALER.TRANSFER.SEARCH_PLACEHOLDER')"
                class="w-full px-3 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-teal-8 no-margin"
                @keydown.enter.prevent="handleTransferQueryEnter"
              />
            </div>

            <!-- Destinos: digitado, ramais e filas -->
            <div class="flex-1 overflow-y-auto min-h-0">
              <div v-if="showTransferDialTyped" class="px-2 pt-1">
                <button
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left"
                  :class="
                    transferTarget === transferQueryNumber
                      ? 'bg-n-teal-3 dark:bg-n-teal-9/20'
                      : 'hover:bg-n-alpha-1'
                  "
                  @click="transferTarget = transferQueryNumber"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-n-solid-3"
                  >
                    <span
                      class="i-lucide-phone-forwarded size-4 text-n-slate-11"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{
                        t('DIALER.TRANSFER.DIAL_TYPED', {
                          number: transferQueryNumber,
                        })
                      }}
                    </p>
                    <p class="text-xs text-n-slate-10">
                      {{ t('DIALER.TRANSFER.DIAL_TYPED_HINT') }}
                    </p>
                  </div>
                  <span
                    v-if="transferTarget === transferQueryNumber"
                    class="i-lucide-check size-4 text-n-teal-11"
                  />
                </button>
              </div>
              <div
                v-if="transferExtensionsFiltered.length > 0"
                class="px-2 pb-2"
              >
                <p
                  class="px-2 py-1.5 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
                >
                  Ramais
                </p>
                <button
                  v-for="ext in transferExtensionsFiltered"
                  :key="ext.id || ext.extension"
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left"
                  :class="
                    transferTarget === String(ext.extension)
                      ? 'bg-n-teal-3 dark:bg-n-teal-9/20'
                      : 'hover:bg-n-alpha-1'
                  "
                  @click="transferTarget = String(ext.extension)"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-n-solid-3"
                  >
                    <span class="i-lucide-headphones size-4 text-n-slate-11" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{
                        ext.caller_id_name ||
                        ext.display_name ||
                        'Ramal ' + ext.extension
                      }}
                    </p>
                    <p class="text-xs text-n-slate-10">{{ ext.extension }}</p>
                  </div>
                  <span
                    v-if="transferTarget === String(ext.extension)"
                    class="i-lucide-check size-4 text-n-teal-11"
                  />
                </button>
              </div>
              <div v-if="transferQueueRoutes.length > 0" class="px-2 pb-2">
                <p
                  class="px-2 py-1.5 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('DIALER.TRANSFER.QUEUES') }}
                </p>
                <button
                  v-for="route in transferQueueRoutes"
                  :key="route.id || route.shortcode"
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left"
                  :class="
                    transferTarget === String(route.shortcode)
                      ? 'bg-n-teal-3 dark:bg-n-teal-9/20'
                      : 'hover:bg-n-alpha-1'
                  "
                  @click="transferTarget = String(route.shortcode)"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-n-solid-3"
                  >
                    <span class="i-lucide-users size-4 text-n-slate-11" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{
                        route.description || route.queue_name || route.shortcode
                      }}
                    </p>
                    <p class="text-xs text-n-slate-10">{{ route.shortcode }}</p>
                  </div>
                  <span
                    v-if="transferTarget === String(route.shortcode)"
                    class="i-lucide-check size-4 text-n-teal-11"
                  />
                </button>
              </div>
              <p
                v-if="
                  transferQuery &&
                  !showTransferDialTyped &&
                  transferExtensionsFiltered.length === 0 &&
                  transferQueueRoutes.length === 0
                "
                class="px-4 py-6 text-xs text-center text-n-slate-10"
              >
                {{ t('DIALER.TRANSFER.NO_RESULTS') }}
              </p>
            </div>

            <!-- Action buttons -->
            <div class="px-4 py-3 border-t border-n-weak flex-shrink-0">
              <div class="flex gap-2">
                <Button
                  color="slate"
                  variant="ghost"
                  class="flex-1"
                  @click="
                    showTransferModal = false;
                    transferMode = 'blind';
                  "
                >
                  Cancelar
                </Button>
                <Button
                  color="teal"
                  class="flex-1"
                  :disabled="!transferTarget || isTransferring"
                  :is-loading="isTransferring"
                  @click="confirmTransfer"
                >
                  {{ transferMode === 'blind' ? 'Transferir' : 'Consultar' }}
                </Button>
              </div>
            </div>
          </template>

          <!-- ===== PHASE 2: Consultation active ===== -->
          <template v-else>
            <!-- Header (no close button during consultation) -->
            <div
              class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0"
            >
              <h4 class="text-sm font-semibold text-n-slate-12">
                Consulta em Andamento
              </h4>
            </div>

            <!-- Consultation status -->
            <div
              class="flex-1 flex flex-col items-center justify-center px-4 py-6"
            >
              <div class="flex flex-col items-center gap-4">
                <!-- Status indicator -->
                <div class="relative">
                  <div
                    class="w-16 h-16 rounded-full flex items-center justify-center"
                    :class="{
                      'bg-n-amber-3 dark:bg-n-amber-9/20':
                        consultationState === 'calling' ||
                        consultationState === 'ringing',
                      'bg-n-teal-3 dark:bg-n-teal-9/20':
                        consultationState === 'connected',
                    }"
                  >
                    <span
                      v-if="
                        consultationState === 'calling' ||
                        consultationState === 'ringing'
                      "
                      class="i-lucide-phone-outgoing size-8 text-n-amber-11 animate-pulse"
                    />
                    <span
                      v-else-if="consultationState === 'connected'"
                      class="i-lucide-phone-call size-8 text-n-teal-11"
                    />
                  </div>
                  <div
                    v-if="
                      consultationState === 'calling' ||
                      consultationState === 'ringing'
                    "
                    class="absolute -inset-2 rounded-full border-2 border-n-amber-6 animate-ping opacity-40"
                  />
                </div>

                <!-- Status text -->
                <div class="text-center">
                  <p class="text-sm text-n-slate-10 mb-1">
                    {{
                      consultationState === 'calling'
                        ? 'Chamando...'
                        : consultationState === 'ringing'
                          ? 'Chamando...'
                          : consultationState === 'connected'
                            ? 'Consulta conectada'
                            : 'Consultando...'
                    }}
                  </p>
                  <p class="text-lg font-semibold text-n-slate-12">
                    {{
                      consultationDisplayName ||
                      consultationTarget ||
                      transferTarget
                    }}
                  </p>
                  <p
                    v-if="consultationSubtitle"
                    class="text-xs text-n-slate-10 mt-0.5"
                  >
                    {{ consultationSubtitle }}
                  </p>
                  <p class="text-xs text-n-slate-10 mt-1">
                    Chamada original em espera
                  </p>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="px-4 py-3 border-t border-n-weak flex-shrink-0">
              <div class="flex gap-2">
                <Button
                  color="slate"
                  variant="ghost"
                  class="flex-1"
                  :disabled="isCancellingConsultation"
                  :is-loading="isCancellingConsultation"
                  @click="handleCancelConsultation"
                >
                  Cancelar
                </Button>
                <Button
                  color="teal"
                  class="flex-1"
                  :disabled="
                    consultationState !== 'connected' || isCompletingTransfer
                  "
                  :is-loading="isCompletingTransfer"
                  @click="handleCompleteAttendedTransfer"
                >
                  Transferir
                </Button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
